import { useRef, useMemo, useCallback, useContext, useEffect } from 'react';
import type { LeafletEventHandlerFnMap, LeafletMouseEvent } from 'leaflet';
import { v4 as uuidv4 } from 'uuid';

import {
  useAppDispatch,
  useAppSelector,
  activeImageSelector,
  editModeSelector,
  editedSpaceSelector,
  editedSpacePositionSelector,
  editedSpaceIdSelector,
  markerEditingSpaceIdSelector,
  spacePositionsSelector,
  moveSpaceInImage,
  setEditedSpace,
  setMarkerEditingSpaceId,
  toggleSelectedWaypoint,
  isSelectingWaypointsSelector,
  mapSpacesSelector,
  SpaceType,
  // Path drawing
  drawingFromNodeIdSelector,
  setDrawingFromNodeId,
  currentLayerNodesSelector,
  addNode,
  addEdge,
  // Navigation
  currentLayerEdgesSelector,
  placementIdToSpaceIdSelector,
  overlayBySpaceIdSelector,
} from '../../../../store';

import { LeafletContext } from '../../../../App';
import { MapEventHandler } from '../../../Map/MapEventHandler';
import { MarkerSelector } from '../../../markers/MarkerSelector';

import { Space } from './Space';

export function SpaceEditing() {
  const { setHoveredMarker } = useContext(LeafletContext);

  const dispatch = useAppDispatch();
  const editMode = useAppSelector(editModeSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const editedSpace = useAppSelector(editedSpaceSelector);
  const editedSpacePosition = useAppSelector(editedSpacePositionSelector);
  const editedSpaceId = useAppSelector(editedSpaceIdSelector);
  const markerEditingSpaceId = useAppSelector(markerEditingSpaceIdSelector);
  const isSelectingWaypoints = useAppSelector(isSelectingWaypointsSelector);

  // Path drawing state
  const drawingFromNodeId = useAppSelector(drawingFromNodeIdSelector);
  const pathNodes = useAppSelector(currentLayerNodesSelector);
  const pathEdges = useAppSelector(currentLayerEdgesSelector);

  const placedSpaces = useAppSelector(mapSpacesSelector);
  const storeSpacePositions = useAppSelector(spacePositionsSelector);
  const placementIdToSpaceId = useAppSelector(placementIdToSpaceIdSelector);
  const overlayBySpaceId = useAppSelector(overlayBySpaceIdSelector);

  const spaces = useMemo(() => editedSpace && editedSpace.id === 'newSpace' ? [...placedSpaces, editedSpace] : placedSpaces, [placedSpaces, editedSpace]);
  const spacePositions = useMemo(
    () => editedSpace && editedSpacePosition
      ? { ...storeSpacePositions, [editedSpace.id]: { x: editedSpacePosition.lat, y: editedSpacePosition.lng, layerId: editedSpacePosition.layerId } }
      : storeSpacePositions,
    [storeSpacePositions, editedSpace, editedSpacePosition],
  );

  const state = useRef<{ mode: null | 'dragging' | 'editing-marker' }>({ mode: null });

  const markerEditingSpace = useMemo(
    () => (markerEditingSpaceId ? spaces.find((s) => s.id === markerEditingSpaceId) : undefined),
    [markerEditingSpaceId, spaces],
  );

  // Reset the internal state if the edited space changes externally (such as when changing layers)
  useEffect(() => {
    if (markerEditingSpaceId === null) state.current.mode = null;
  }, [markerEditingSpaceId]);

  // Close marker selector when space is no longer found (e.g. after save when IDs change)
  useEffect(() => {
    if (markerEditingSpaceId && !markerEditingSpace) {
      dispatch(setMarkerEditingSpaceId(null));
      state.current.mode = null;
    }
  }, [markerEditingSpaceId, markerEditingSpace, dispatch]);

  // Helper to resolve a spaceId that might be a placement UUID
  const resolveSpaceId = useCallback((spaceId: string | undefined): string | undefined => {
    if (!spaceId) return undefined;
    const spaceIdStr = String(spaceId);
    // If it's already a valid space ID, return it
    if (spacePositions[spaceIdStr]) return spaceIdStr;
    // Otherwise check if it's a placement UUID and map it
    const mapped = placementIdToSpaceId[spaceIdStr];
    return mapped || spaceIdStr;
  }, [spacePositions, placementIdToSpaceId]);

  // Helper to find a space ID by position (for matching nodes after backend transforms IDs)
  // Use very tight threshold - only for nearly exact position matches
  const findSpaceIdByPosition = (pos: { x: number; y: number }): string | undefined => {
    const threshold = 0.001; // Very tight - only for exact matches
    for (const [spaceId, spacePos] of Object.entries(spacePositions)) {
      if (Math.abs(spacePos.x - pos.x) < threshold &&
        Math.abs(spacePos.y - pos.y) < threshold) {
        return spaceId;
      }
    }
    return undefined;
  };

  // Helper to find a node by space ID (handles placement UUID mapping + position fallback)
  const findNodeBySpaceId = useCallback((spaceId: string): typeof pathNodes[0] | undefined => {
    const spaceIdStr = String(spaceId);
    // First try direct match
    const node = pathNodes.find((n) => n.spaceId && String(n.spaceId) === spaceIdStr);
    if (node) return node;
    // If not found, maybe the node has a placement UUID - find by resolving
    const resolvedNode = pathNodes.find((n) => {
      if (!n.spaceId) return false;
      const resolved = resolveSpaceId(n.spaceId);
      return resolved === spaceIdStr;
    });
    if (resolvedNode) return resolvedNode;
    return undefined;
  }, [pathNodes, resolveSpaceId]);

  // Track pending edge creation (from nodeId/spaceId to spaceId)
  const pendingEdge = useRef<{ fromSpaceId: string; toSpaceId: string } | null>(null);

  // Effect to complete edge creation after nodes are added
  useEffect(() => {
    if (pendingEdge.current && pathNodes.length > 0) {
      const { fromSpaceId, toSpaceId } = pendingEdge.current;

      // Find the "from" node - could be a node ID or spaceId
      let fromNode = pathNodes.find((n) => n.id === fromSpaceId);
      if (!fromNode) {
        fromNode = findNodeBySpaceId(fromSpaceId);
      }

      // Find the "to" node by spaceId
      const toNode = findNodeBySpaceId(toSpaceId);

      if (fromNode && toNode) {
        dispatch(addEdge({
          from: fromNode.id,
          to: toNode.id,
          bidirectional: true,
        }));
        pendingEdge.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathNodes, dispatch]);

  const mapEventHandlers = useMemo<LeafletEventHandlerFnMap>(() => ({
    click: (e) => {
      if (state.current.mode === null) {
        if (editedSpace) {
          dispatch(setEditedSpace(null));
        } else {
          const { lat, lng } = e.latlng;
          if (editMode === 'pin') {
            dispatch(setEditedSpace({ id: 'newSpace', position: { lat, lng, layerId: activeImage?.id } }));
          } else if (editMode === 'drawingPath') {
            // Handle path drawing mode - clicking on empty map creates waypoint
            if (activeImage) {
              const { lat, lng } = e.latlng;

              // Create a new waypoint node (no spaceId) with a known ID
              const newNodeId = uuidv4();
              dispatch(addNode({
                id: newNodeId,
                position: { x: lat, y: lng },
                layerId: activeImage.id,
                // No spaceId - this is a waypoint
              }));

              if (drawingFromNodeId) {
                // Connect from the previous node to this new waypoint
                // Find the actual node id to connect from
                let fromNodeId = drawingFromNodeId;

                // If it's a space reference, find the node
                if (drawingFromNodeId.startsWith('space:')) {
                  const spaceId = drawingFromNodeId.replace('space:', '');
                  const spaceNode = pathNodes.find((n) => n.spaceId && String(n.spaceId) === String(spaceId));
                  if (spaceNode) {
                    fromNodeId = spaceNode.id;
                  }
                }

                // Create the edge connecting the previous node to this new waypoint
                dispatch(addEdge({
                  from: fromNodeId,
                  to: newNodeId,
                  bidirectional: true,
                }));
              }

              // Set this new node as the starting point for next connection
              dispatch(setDrawingFromNodeId(newNodeId));
              return;
            }
          }
        }
      }
    },
    dragstart: (e) => {
      e.target._container.classList.add('dragging-map');
    },
    dragend: (e) => {
      e.target._container.classList.remove('dragging-map');
    },
  }), [editedSpace, dispatch, activeImage, drawingFromNodeId, pathNodes, editMode]);

  const spaceEventHandlers = useMemo(() => (space: SpaceType): LeafletEventHandlerFnMap => ({
    click: (e) => {
      e.target.closePopup();

      // Handle waypoint selection - allow selecting ANY space, not just marked waypoints
      if (isSelectingWaypoints) {
        e.target.closePopup();
        dispatch(toggleSelectedWaypoint(space.id));
      }
    },

    contextmenu: (e: LeafletMouseEvent) => {
      e.originalEvent.preventDefault();
      // Prevevent opening marker selector when we're already editing another marker or placing a new space
      if (state.current.mode === 'editing-marker' || space.id === 'newSpace') return;
      if (editedSpaceId && editedSpaceId !== space.id) {
        // Sync the space edited in sidebar and the marker selector one
        dispatch(setEditedSpace({
          id: space.id,
          position: {
            lat: +spacePositions[space.id].x,
            lng: +spacePositions[space.id].y,
            layerId: spacePositions[space.id].layerId,
          },
        }));
      }
      state.current.mode = 'editing-marker';
      dispatch(setMarkerEditingSpaceId(space.id));
    },

    mouseup: (e) => {
      if (state.current.mode !== null) return;

      // Handle path drawing mode
      if (editMode === 'drawingPath' && activeImage) {
        e.target.closePopup();
        const position = spacePositions[space.id];

        // Check if this space already has a node (handles placement UUID mapping)

        const existingNode = findNodeBySpaceId(space.id);

        if (!drawingFromNodeId) {
          // Starting a new path
          if (!existingNode) {
            // Create new node for this space
            dispatch(addNode({
              spaceId: String(space.id),
              position: { x: position.x, y: position.y },
              layerId: activeImage.id,
            }));
          }
          // Store the spaceId as the starting point
          dispatch(setDrawingFromNodeId(`space:${space.id}`));
        } else {
          // Completing a path - connecting from previous node to this space

          // Don't connect to same space
          if (drawingFromNodeId === `space:${space.id}`) {
            dispatch(setDrawingFromNodeId(null));
            return;
          }

          // Create destination node if needed
          if (!existingNode) {
            dispatch(addNode({
              spaceId: String(space.id),
              position: { x: position.x, y: position.y },
              layerId: activeImage.id,
            }));
          }

          // Find the "from" node
          let fromNode;
          if (drawingFromNodeId.startsWith('space:')) {
            // Coming from another space - use helper for mapping
            const fromSpaceId = drawingFromNodeId.replace('space:', '');
            fromNode = findNodeBySpaceId(fromSpaceId);
          } else {
            // Coming from a waypoint node
            fromNode = pathNodes.find((n) => n.id === drawingFromNodeId);
          }

          // Try to create edge immediately if both nodes exist
          const toNode = existingNode || findNodeBySpaceId(space.id);
          if (fromNode && toNode) {
            dispatch(addEdge({
              from: fromNode.id,
              to: toNode.id,
              bidirectional: true,
            }));
          } else {
            // Schedule edge creation for when nodes exist
            pendingEdge.current = {
              fromSpaceId: drawingFromNodeId.startsWith('space:')
                ? drawingFromNodeId.replace('space:', '')
                : drawingFromNodeId,
              toSpaceId: String(space.id),
            };
          }

          // Stop drawing after connecting to second marker
          dispatch(setDrawingFromNodeId(null));
        }
        return;
      }

      // Handle waypoint selection - allow selecting ANY space, not just marked waypoints
      if (isSelectingWaypoints) {
        e.target.closePopup();
        dispatch(toggleSelectedWaypoint(space.id));
      } else if (editMode === 'marker') {
        state.current.mode = 'editing-marker';
        dispatch(setMarkerEditingSpaceId(space.id));
      } else {
        dispatch(setEditedSpace({
          id: space.id,
          position: {
            lat: +spacePositions[space.id].x,
            lng: +spacePositions[space.id].y,
            layerId: spacePositions[space.id].layerId,
          },
        }));
      }
    },

    mouseover: () => state.current.mode === null && setHoveredMarker(space.id),
    mouseout: () => state.current.mode === null && setHoveredMarker(''),

    // Prevent opening edit form when dragging
    drag: () => (state.current.mode = 'dragging'),
    // Update marker position after dragging
    dragend: (e) => {
      const { lat: x, lng: y } = e.target._latlng;
      if (editedSpaceId === space.id) {
        dispatch(setEditedSpace({
          id: space.id,
          position: {
            lat: +x,
            lng: +y,
            layerId: spacePositions[space.id].layerId,
          },
        }));
      } else {
        dispatch(moveSpaceInImage({ id: space.id, x, y }));
      }
      state.current.mode = null;
    },
  }), [dispatch, isSelectingWaypoints, editedSpaceId, spacePositions, setHoveredMarker, editMode, drawingFromNodeId, pathNodes, activeImage, findNodeBySpaceId]);

  // Get all reachable spaces from a given space via the path network (BFS traversal)
  const getConnectedSpaces = (spaceId: string): SpaceType[] => {
    const spaceNode = pathNodes.find((n) => n.spaceId === spaceId);
    if (!spaceNode) return [];

    // Build adjacency list for graph traversal
    const adjacency: Record<string, string[]> = {};
    pathNodes.forEach((n) => {
      adjacency[n.id] = [];
    });
    pathEdges.forEach((e) => {
      if (adjacency[e.from]) adjacency[e.from].push(e.to);
      if (adjacency[e.to]) adjacency[e.to].push(e.from); // bidirectional
    });

    // BFS to find all reachable nodes
    const visited = new Set<string>();
    const queue = [spaceNode.id];
    visited.add(spaceNode.id);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const neighbors = adjacency[current] || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    // Get resolved space IDs for all reachable nodes (excluding the starting space)
    const reachableSpaceIds = Array.from(visited)
      .map((nodeId) => {
        const node = pathNodes.find((n) => n.id === nodeId);
        if (!node?.spaceId) return undefined;
        // Try to resolve using ID mapping first
        const resolved = resolveSpaceId(node.spaceId);
        if (resolved && spacePositions[resolved]) return resolved;
        // Fallback: try to find space by position
        return findSpaceIdByPosition(node.position);
      })
      .filter((id): id is string => !!id && id !== String(spaceId));

    // Return the actual space objects
    return placedSpaces.filter((s) => reachableSpaceIds.includes(String(s.id)));
  };

  return (
    <>
      <MapEventHandler
        enabled={editMode === 'pin' || editMode === 'drawingPath'}
        handlers={mapEventHandlers}
      />
      {spaces.map((space) => space.id !== markerEditingSpaceId && (
        <Space
          key={space.id}
          space={space}
          position={[spacePositions[space.id].x, spacePositions[space.id].y]}
          hiddenMarker={!!overlayBySpaceId[space.id]}
          eventHandlers={spaceEventHandlers(space)}
          connectedSpaces={getConnectedSpaces(space.id)}
          findNodeBySpaceId={findNodeBySpaceId}
        />
      ))}
      {markerEditingSpace && markerEditingSpaceId && (
        <MarkerSelector
          space={markerEditingSpace}
          position={[spacePositions[markerEditingSpaceId].x, spacePositions[markerEditingSpaceId].y]}
          onClose={() => {
            setHoveredMarker('');
            dispatch(setMarkerEditingSpaceId(null));
            state.current.mode = null;
          }}
        />
      )}
    </>
  );
}
